/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useQuery } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';

/**
 * `BudgetIndicators` type.
 */

export type BudgetIndicators = {
  initialPrevision: BigNumber;
  currentPrevision: BigNumber;
  budgetDiff: BigNumber;
  closed: BigNumber;
  initialClosed: BigNumber;
  closedDiff: BigNumber;
  open: BigNumber;
  paid: BigNumber;
  debt: BigNumber;
  deviation: BigNumber;
  newPrevision: BigNumber;
  toComplete: BigNumber;
  items: { id: number; name: string; allocated: number; consumed: number; remaining: number }[];
};

/**
 * `ProjectOverview` type.
 */

export type ProjectOverview = {
  budgets: BudgetIndicators;
  movements: { total: number; recent: any[] };
  proposals: { total: number; value: BigNumber; accepted: number; pending: number };
};

/**
 * Read a CRUD list response as an array.
 */

function asArray(payload: any): any[] {
  return payload?.results ?? payload ?? [];
}

/**
 * Sum a budget's proposals (mirrors the budgets footer): closed budgets without
 * a proposal fall back to their consumed value; budgets with no proposal at all
 * return null (they count as "open").
 */

function sumProposals(budget: any): BigNumber | null {
  if (budget.withoutProposal) {
    return new BigNumber(budget.totalMovementTax ?? 0);
  }

  if (!budget.proposals || budget.proposals.length === 0) {
    return null;
  }

  return budget.proposals.reduce((acc: BigNumber, proposal: any) => acc.plus(proposal.total ?? 0), new BigNumber(0));
}

/**
 * Compute the budget indicators from the budgets (with proposals).
 */

function computeIndicators(budgets: any[]): BudgetIndicators {
  let initialPrevision = new BigNumber(0);
  let currentPrevision = new BigNumber(0);
  let closed = new BigNumber(0);
  let initialClosed = new BigNumber(0);
  let open = new BigNumber(0);
  let paid = new BigNumber(0);
  let debt = new BigNumber(0);
  let deviation = new BigNumber(0);

  for (const budget of budgets) {
    const proposalsTotal = sumProposals(budget);
    const hasProposals = !!budget.proposals && budget.proposals.length > 0;

    initialPrevision = initialPrevision.plus(budget.prevision ?? 0);
    currentPrevision = currentPrevision.plus(proposalsTotal ?? budget.prevision ?? 0);

    if (proposalsTotal != null) {
      closed = closed.plus(proposalsTotal);
      initialClosed = initialClosed.plus(budget.prevision ?? 0);
    }

    if (!hasProposals && !budget.withoutProposal) {
      open = open.plus(budget.prevision ?? 0);
    }

    paid = paid.plus(budget.paid ?? 0);
    debt = debt.plus(budget.debt ?? 0);

    if (hasProposals && budget.diffOverBudget) {
      deviation = deviation.plus(budget.diffOverBudget);
    }
  }

  const newPrevision = initialPrevision.minus(deviation);

  return {
    initialPrevision,
    currentPrevision,
    budgetDiff: initialPrevision.minus(currentPrevision),
    closed,
    initialClosed,
    closedDiff: initialClosed.minus(closed),
    open,
    paid,
    debt,
    deviation,
    newPrevision,
    toComplete: newPrevision.minus(paid),
    items: budgets.map((budget: any) => ({
      id: budget.id,
      name: budget.name,
      allocated: parseFloat(budget.prevision ?? '0'),
      consumed: parseFloat(budget.totalMovementTax ?? '0'),
      remaining: parseFloat(budget.diffOverBudget ?? '0')
    }))
  };
}

/**
 * Normalize the (string) server payload into BigNumber indicators.
 */

function normalizeServerOverview(data: any): ProjectOverview {
  const b = data.budgets ?? {};
  const bn = (value: any) => new BigNumber(value ?? 0);

  return {
    budgets: {
      initialPrevision: bn(b.initialPrevision),
      currentPrevision: bn(b.currentPrevision),
      budgetDiff: bn(b.budgetDiff),
      closed: bn(b.closed),
      initialClosed: bn(b.initialClosed),
      closedDiff: bn(b.closedDiff),
      open: bn(b.open),
      paid: bn(b.paid),
      debt: bn(b.debt),
      deviation: bn(b.deviation),
      newPrevision: bn(b.newPrevision),
      toComplete: bn(b.toComplete),
      items: (b.items ?? []).map((item: any) => ({
        id: item.id,
        name: item.name,
        allocated: parseFloat(item.allocated ?? '0'),
        consumed: parseFloat(item.consumed ?? '0'),
        remaining: parseFloat(item.remaining ?? '0')
      }))
    },
    movements: { total: data.movements?.total ?? 0, recent: data.movements?.recent ?? [] },
    proposals: {
      total: data.proposals?.total ?? 0,
      value: bn(data.proposals?.value),
      accepted: data.proposals?.accepted ?? 0,
      pending: data.proposals?.pending ?? 0
    }
  };
}

/**
 * Export `useProjectOverview`.
 *
 * Prefers the dedicated `/projects/:id/overview` endpoint; falls back to
 * aggregating the list endpoints client-side (budgets already carry proposals +
 * stored balances), so it works across deploy ordering.
 */

export function useProjectOverview(projectId: string | number | undefined) {
  const client = useRequestProvider();

  return useQuery<ProjectOverview>(['projectOverview', projectId], {
    enabled: projectId !== undefined,
    queryFn: async () => {
      const get = (key: any, params?: any) =>
        client
          .get(resolveAppUrl(key, { interpolations: { projectId }, params }))
          .then(({ data }: { data: any }) => data?.data);

      try {
        const data = await get('projectOverview');

        if (data?.budgets?.initialPrevision !== undefined) {
          return normalizeServerOverview(data);
        }
      } catch {
        // fall through to client-side aggregation
      }

      const [budgetsRaw, movementsRaw, proposalsRaw] = await Promise.all([
        get('projectBudgets'),
        get('projectMovements', { page: 1, pageSize: 5, order: [{ key: 'date', direction: 'desc' }] }),
        get('projectProposals')
      ]);

      const proposals = asArray(proposalsRaw);

      return {
        budgets: computeIndicators(asArray(budgetsRaw)),
        movements: {
          total: movementsRaw?.total ?? asArray(movementsRaw).length,
          recent: asArray(movementsRaw)
        },
        proposals: {
          total: proposals.length,
          value: proposals.reduce((acc: BigNumber, item: any) => acc.plus(item.total ?? 0), new BigNumber(0)),
          accepted: proposals.filter((item: any) => item.selectedBudgetId != null).length,
          pending: proposals.filter((item: any) => item.selectedBudgetId == null).length
        }
      };
    }
  });
}
